import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBomb, 
  faFlag, 
  faTrophy,
  faFaceSmile,
  faFaceFrown,
  faFaceSurprise,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";

type Difficulty = "easy" | "medium" | "hard";
type CellState = "hidden" | "revealed" | "flagged";

interface Cell {
  hasMine: boolean;
  neighborMines: number;
  state: CellState;
}

interface GameConfig {
  rows: number;
  cols: number;
  mines: number;
}

interface GameStats {
  gamesPlayed: number;
  gamesWon: number;
  bestTime: number | null;
  currentStreak: number;
  bestStreak: number;
}

const DIFFICULTIES: Record<Difficulty, GameConfig> = {
  easy: { rows: 9, cols: 9, mines: 10 },
  medium: { rows: 16, cols: 16, mines: 40 },
  hard: { rows: 16, cols: 30, mines: 99 },
};

const STORAGE_KEY = "minesweeper-stats";

/**
 * Minesweeper game component with full scoring, timer, and statistics
 */
export function Minesweeper() {
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [board, setBoard] = useState<Cell[][]>([]);
  const [gameStatus, setGameStatus] = useState<"idle" | "playing" | "won" | "lost">("idle");
  const [flagsLeft, setFlagsLeft] = useState(0);
  const [time, setTime] = useState(0);
  const [stats, setStats] = useState<GameStats>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {
      gamesPlayed: 0,
      gamesWon: 0,
      bestTime: null,
      currentStreak: 0,
      bestStreak: 0,
    };
  });

  const config = DIFFICULTIES[difficulty];

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  }, [stats]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStatus === "playing") {
      interval = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [gameStatus]);

  const createEmptyBoard = useCallback((config: GameConfig): Cell[][] => {
    return Array.from({ length: config.rows }, () =>
      Array.from({ length: config.cols }, () => ({
        hasMine: false,
        neighborMines: 0,
        state: "hidden" as CellState,
      }))
    );
  }, []);

  const placeMines = useCallback((board: Cell[][], config: GameConfig, firstRow: number, firstCol: number) => {
    const newBoard = board.map(row => row.map(cell => ({ ...cell })));
    let minesPlaced = 0;

    while (minesPlaced < config.mines) {
      const row = Math.floor(Math.random() * config.rows);
      const col = Math.floor(Math.random() * config.cols);
      
      const isFirstClick = row === firstRow && col === firstCol;
      const isNeighbor = Math.abs(row - firstRow) <= 1 && Math.abs(col - firstCol) <= 1;
      
      if (!newBoard[row][col].hasMine && !isFirstClick && !isNeighbor) {
        newBoard[row][col].hasMine = true;
        minesPlaced++;
      }
    }

    for (let row = 0; row < config.rows; row++) {
      for (let col = 0; col < config.cols; col++) {
        if (!newBoard[row][col].hasMine) {
          let count = 0;
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc === 0) continue;
              const nr = row + dr;
              const nc = col + dc;
              if (nr >= 0 && nr < config.rows && nc >= 0 && nc < config.cols) {
                if (newBoard[nr][nc].hasMine) count++;
              }
            }
          }
          newBoard[row][col].neighborMines = count;
        }
      }
    }

    return newBoard;
  }, []);

  const initGame = useCallback(() => {
    const emptyBoard = createEmptyBoard(config);
    setBoard(emptyBoard);
    setGameStatus("idle");
    setFlagsLeft(config.mines);
    setTime(0);
  }, [config, createEmptyBoard]);

  useEffect(() => {
    initGame();
  }, []);

  const revealCell = useCallback((board: Cell[][], row: number, col: number): Cell[][] => {
    if (row < 0 || row >= board.length || col < 0 || col >= board[0].length) return board;
    if (board[row][col].state !== "hidden") return board;

    const newBoard = board.map(r => r.map(c => ({ ...c })));
    newBoard[row][col].state = "revealed";

    if (newBoard[row][col].neighborMines === 0 && !newBoard[row][col].hasMine) {
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          newBoard.splice(0, newBoard.length, ...revealCell(newBoard, row + dr, col + dc));
        }
      }
    }

    return newBoard;
  }, []);

  const handleCellClick = useCallback((row: number, col: number) => {
    if (gameStatus === "won" || gameStatus === "lost") return;
    if (board[row][col].state === "flagged") return;

    if (gameStatus === "idle") {
      const boardWithMines = placeMines(board, config, row, col);
      const revealedBoard = revealCell(boardWithMines, row, col);
      setBoard(revealedBoard);
      setGameStatus("playing");
      return;
    }

    if (board[row][col].hasMine) {
      const newBoard = board.map(r => r.map(c => ({ ...c, state: c.hasMine ? "revealed" as CellState : c.state })));
      setBoard(newBoard);
      setGameStatus("lost");
      setStats(prev => ({
        ...prev,
        gamesPlayed: prev.gamesPlayed + 1,
        currentStreak: 0,
      }));
      return;
    }

    const newBoard = revealCell(board, row, col);
    setBoard(newBoard);

    const revealedCount = newBoard.flat().filter(c => c.state === "revealed").length;
    const totalCells = config.rows * config.cols;
    if (revealedCount === totalCells - config.mines) {
      setGameStatus("won");
      setStats(prev => {
        const newStreak = prev.currentStreak + 1;
        return {
          gamesPlayed: prev.gamesPlayed + 1,
          gamesWon: prev.gamesWon + 1,
          bestTime: prev.bestTime === null ? time : Math.min(prev.bestTime, time),
          currentStreak: newStreak,
          bestStreak: Math.max(prev.bestStreak, newStreak),
        };
      });
    }
  }, [board, gameStatus, config, placeMines, revealCell, time]);

  const handleCellRightClick = useCallback((e: React.MouseEvent, row: number, col: number) => {
    e.preventDefault();
    if (gameStatus === "won" || gameStatus === "lost" || gameStatus === "idle") return;
    if (board[row][col].state === "revealed") return;

    const newBoard = board.map(r => r.map(c => ({ ...c })));
    if (newBoard[row][col].state === "hidden") {
      newBoard[row][col].state = "flagged";
      setFlagsLeft(prev => prev - 1);
    } else if (newBoard[row][col].state === "flagged") {
      newBoard[row][col].state = "hidden";
      setFlagsLeft(prev => prev + 1);
    }
    setBoard(newBoard);
  }, [board, gameStatus]);

  const getCellContent = (cell: Cell) => {
    if (cell.state === "flagged") {
      return <FontAwesomeIcon icon={faFlag} className="text-red-500" />;
    }
    if (cell.state === "hidden") {
      return null;
    }
    if (cell.hasMine) {
      return <FontAwesomeIcon icon={faBomb} className="text-gray-900 dark:text-gray-100" />;
    }
    if (cell.neighborMines === 0) {
      return null;
    }
    return cell.neighborMines;
  };

  const getCellColor = (neighborMines: number) => {
    const colors = [
      "",
      "text-[#0000FF]",
      "text-[#008000]",
      "text-[#FF0000]",
      "text-[#000080]",
      "text-[#800000]",
      "text-[#008080]",
      "text-[#000000]",
      "text-[#808080]",
    ];
    return colors[neighborMines] || "";
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const winRate = stats.gamesPlayed > 0 
    ? Math.round((stats.gamesWon / stats.gamesPlayed) * 100) 
    : 0;

  const getFaceIconClass = () => {
    if (gameStatus === "won") return "text-green-800";
    if (gameStatus === "lost") return "text-red-800";
    return "text-amber-900";
  };

  return (
    <div className="flex items-center justify-center h-full bg-transparent p-6 pt-8 overflow-hidden">
      <div>
        {/* Classic Windows Minesweeper Board */}
        <div 
          className="inline-block bg-[#C0C0C0]"
          style={{
            padding: "12px",
            border: "3px solid",
            borderColor: "#FFFFFF #808080 #808080 #FFFFFF",
            boxShadow: "inset 0 0 0 1px #C0C0C0",
          }}
        >
          {/* Top Panel - Counter, Face, Timer */}
          <div 
            className="flex justify-between items-center bg-[#C0C0C0]"
            style={{
              marginBottom: "12px",
              padding: "5px 8px",
              border: "3px solid",
              borderColor: "#808080 #FFFFFF #FFFFFF #808080",
            }}
          >
            {/* Mines Counter */}
            <div 
              className="bg-black font-mono text-[32px] font-bold text-[#FF0000] leading-none"
              style={{
                width: "52px",
                height: "26px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #808080",
                fontFamily: "'Courier New', monospace",
                letterSpacing: "-2px",
              }}
            >
              {String(Math.max(0, flagsLeft)).padStart(3, "0")}
            </div>

            {/* Smiley Face Button */}
            <button
              onClick={initGame}
              className="bg-[#C0C0C0] flex items-center justify-center"
              style={{
                width: "26px",
                height: "26px",
                border: "2px solid",
                borderColor: "#FFFFFF #808080 #808080 #FFFFFF",
                fontSize: "16px",
                padding: 0,
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.borderColor = "#808080 #FFFFFF #FFFFFF #808080";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.borderColor = "#FFFFFF #808080 #808080 #FFFFFF";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#FFFFFF #808080 #808080 #FFFFFF";
              }}
            >
              <FontAwesomeIcon 
                icon={
                  gameStatus === "won"
                    ? faFaceSmile
                    : gameStatus === "lost"
                      ? faFaceFrown
                      : faFaceSurprise
                }
                className={getFaceIconClass()}
                style={{ fontSize: "18px", lineHeight: 1 }}
              />
            </button>

            {/* Timer */}
            <div 
              className="bg-black font-mono text-[32px] font-bold text-[#FF0000] leading-none"
              style={{
                width: "52px",
                height: "26px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #808080",
                fontFamily: "'Courier New', monospace",
                letterSpacing: "-2px",
              }}
            >
              {String(Math.min(999, time)).padStart(3, "0")}
            </div>
          </div>

          {/* Game Board */}
          <div 
            className="inline-block"
            style={{
              border: "3px solid",
              borderColor: "#808080 #FFFFFF #FFFFFF #808080",
              display: "inline-block",
            }}
          >
            {board.map((row, rowIndex) => (
              <div key={rowIndex} className="flex" style={{ lineHeight: 0 }}>
                {row.map((cell, colIndex) => (
                  <button
                    key={`${rowIndex}-${colIndex}`}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                    onContextMenu={(e) => handleCellRightClick(e, rowIndex, colIndex)}
                    className={`
                      flex items-center justify-center font-bold
                      ${cell.state === "hidden" 
                        ? "bg-[#C0C0C0]" 
                        : cell.hasMine && gameStatus === "lost"
                          ? "bg-red-600"
                          : "bg-[#BDBDBD]"
                      }
                      ${getCellColor(cell.neighborMines)}
                    `}
                    style={{
                      width: "16px",
                      height: "16px",
                      border: cell.state === "hidden" 
                        ? "2px solid"
                        : "1px solid #808080",
                      borderColor: cell.state === "hidden"
                        ? "#FFFFFF #808080 #808080 #FFFFFF"
                        : "#808080",
                      fontFamily: "Arial, sans-serif",
                      fontWeight: "bold",
                      fontSize: "12px",
                      lineHeight: "12px",
                      padding: 0,
                      margin: 0,
                    }}
                    onMouseDown={(e) => {
                      if (cell.state === "hidden" && e.button === 0) {
                        e.currentTarget.style.border = "1px solid #808080";
                      }
                    }}
                    onMouseUp={(e) => {
                      if (cell.state === "hidden") {
                        e.currentTarget.style.border = "2px solid";
                        e.currentTarget.style.borderColor = "#FFFFFF #808080 #808080 #FFFFFF";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (cell.state === "hidden") {
                        e.currentTarget.style.border = "2px solid";
                        e.currentTarget.style.borderColor = "#FFFFFF #808080 #808080 #FFFFFF";
                      }
                    }}
                  >
                    {getCellContent(cell)}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
